import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'

// Layout
import MainLayout from '@/layouts/MainLayout'
import ProtectedRoute from '@/components/ProtectedRoute'
import RequirePermission from '@/components/RequirePermission'
import RequireSoftwareAccess from '@/components/RequireSoftwareAccess'

// Pages
import Dashboard from '@/pages/Dashboard'
import SalesDashboard from '@/pages/Dashboard/SalesDashboard'
import AllocationDashboard from '@/pages/Dashboard/AllocationDashboard'
import FinanceDashboard from '@/pages/Dashboard/FinanceDashboard'
import LoginPage from '@/pages/Auth/LoginPage'
import ForgotPasswordPage from '@/pages/Auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/Auth/ResetPasswordPage'
import SelectSoftwarePage from '@/pages/Auth/SelectSoftwarePage'

// Lead Management
import LeadList from '@/pages/Lead/LeadList'
import LeadForm from '@/pages/Lead/LeadForm'
import LeadDetail from '@/pages/Lead/LeadDetail'

// Proposal Management
import ProposalList from '@/pages/Proposal/ProposalList'
import ProposalForm from '@/pages/Proposal/ProposalForm'
import ProposalDetail from '@/pages/Proposal/ProposalDetail'

// Agreement Management
import AgreementList from '@/pages/Agreement/AgreementList'
import AgreementForm from '@/pages/Agreement/AgreementForm'
import AgreementDetail from '@/pages/Agreement/AgreementDetail'

// Generator-Consumer Mapping
import MappingList from '@/pages/Mapping/MappingList'
import MappingForm from '@/pages/Mapping/MappingForm'
import MappingDetail from '@/pages/Mapping/MappingDetail'

// Meter Readings
import MeterReadingList from '@/pages/MeterReading/MeterReadingList'
import MeterReadingForm from '@/pages/MeterReading/MeterReadingForm'
import MeterReadingDetail from '@/pages/MeterReading/MeterReadingDetail'

// Unit Allocation
import AllocationList from '@/pages/Allocation/AllocationList'
import AllocationForm from '@/pages/Allocation/AllocationForm'

// Invoicing
import InvoiceList from '@/pages/Invoice/InvoiceList'
import InvoiceForm from '@/pages/Invoice/InvoiceForm'
import InvoiceDetail from '@/pages/Invoice/InvoiceDetail'

// Payments
import PaymentList from '@/pages/Payment/PaymentList'
import PaymentForm from '@/pages/Payment/PaymentForm'
import PaymentDetail from '@/pages/Payment/PaymentDetail'

// Generator Payout
import PayoutList from '@/pages/Payout/PayoutList'
import PayoutForm from '@/pages/Payout/PayoutForm'



// Profile
import ProfilePage from '@/pages/Profile/ProfilePage'

// Notifications
import NotificationList from '@/pages/Notification/NotificationList'

// Daily Call Report
import DCRList from '@/pages/DCR/DCRList'
import DCRDetail from '@/pages/DCR/DCRDetail'

// Savings Calculator
import SavingsCalculator from '@/pages/Calculator/SavingsCalculator'

// Masters & Admin
import TariffMaster from '@/pages/Master/TariffMaster'
import TnebCategoryMaster from '@/pages/Master/TnebCategoryMaster'
import TnebTariffMaster from '@/pages/Master/TnebTariffMaster'
import GSTMaster from '@/pages/Master/GSTMaster'
import SlotMaster from '@/pages/Master/SlotMaster'
import GeneratorMaster from '@/pages/Master/GeneratorMaster'
import CustomerMaster from '@/pages/Master/CustomerMaster'
import ProposalTemplatesMaster from '@/pages/Master/ProposalTemplatesMaster'
import EmailTemplateMaster from '@/pages/Master/EmailTemplateMaster'
import WhatsAppTemplateMaster from '@/pages/Master/WhatsAppTemplateMaster'
import CodeMaster from '@/pages/Master/CodeMaster'
import UserMaster from '@/pages/Master/UserMaster'
import RoleMaster from '@/pages/Master/RoleMaster'
import WorkflowMaster from '@/pages/Master/WorkflowMaster'
import BackupPage from '@/pages/Master/BackupPage'
import PowerTypeMaster from '@/pages/Master/PowerTypeMaster'
import SupplyModeMaster from '@/pages/Master/SupplyModeMaster'
import EdcMaster from '@/pages/Master/EdcMaster'
import BankMaster from '@/pages/Master/BankMaster'
import InjectingVoltageMaster from '@/pages/Master/InjectingVoltageMaster'
import DrawalVoltageMaster from '@/pages/Master/DrawalVoltageMaster'
import AgreementTypeMaster from '@/pages/Master/AgreementTypeMaster'


// Reports
import LeadConversionReport from '@/pages/Reports/LeadConversionReport'
import SalesPerformanceReport from '@/pages/Reports/SalesPerformanceReport'
import RevenueReport from '@/pages/Reports/RevenueReport'
import GSTReportReport from '@/pages/Reports/GSTReport'
import PAndLReport from '@/pages/Reports/PAndLReport'

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Standalone (no MainLayout chrome) — shown right after login so the
          user can choose which application to open. */}
      <Route element={<ProtectedRoute />}>
        <Route path="/select-software" element={<SelectSoftwarePage />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<RequireSoftwareAccess code="SHAKTHI_POWER" />}>
        <Route element={<MainLayout />}>
        {/* Dashboard, profile, notifications, and the calculator are utility
            pages available to every signed-in user regardless of module
            permissions — same as before. */}
        <Route path="/" element={<Dashboard />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/notifications" element={<NotificationList />} />
        <Route path="/calculator" element={<SavingsCalculator />} />

        {/* Below: each group is gated on its Role Master module "view"
            permission — previously every route was reachable by URL alone,
            regardless of the signed-in user's role permissions. */}

        {/* Lead Management & Sales Dashboard */}
        <Route element={<RequirePermission module="Leads" />}>
          <Route path="/sales/dashboard" element={<SalesDashboard />} />
          <Route path="/leads" element={<LeadList />} />
          <Route path="/leads/new" element={<LeadForm />} />
          <Route path="/leads/:id" element={<LeadDetail />} />
          <Route path="/leads/:id/edit" element={<LeadForm />} />
        </Route>

        {/* Proposal Management */}
        <Route element={<RequirePermission module="Proposals" />}>
          <Route path="/proposals" element={<ProposalList />} />
          <Route path="/proposals/new" element={<ProposalForm />} />
          <Route path="/proposals/:id" element={<ProposalDetail />} />
          <Route path="/proposals/:id/edit" element={<ProposalForm />} />
        </Route>

        {/* Agreement Management */}
        <Route element={<RequirePermission module="Agreements" />}>
          <Route path="/agreements" element={<AgreementList />} />
          <Route path="/agreements/new" element={<AgreementForm />} />
          <Route path="/agreements/:id" element={<AgreementDetail />} />
          <Route path="/agreements/:id/edit" element={<AgreementForm />} />
        </Route>

        {/* Generator-Consumer Mapping */}
        <Route element={<RequirePermission module="Mapping" />}>
          <Route path="/mappings" element={<MappingList />} />
          <Route path="/mappings/new" element={<MappingForm />} />
          <Route path="/mappings/:id" element={<MappingDetail />} />
          <Route path="/mappings/:id/edit" element={<MappingForm />} />
        </Route>

        {/* Meter Readings, Unit Allocation & Allocation Dashboard */}
        <Route element={<RequirePermission module="Allocations" />}>
          <Route path="/allocations/dashboard" element={<AllocationDashboard />} />
          <Route path="/meter-readings" element={<MeterReadingList />} />
          <Route path="/meter-readings/new" element={<MeterReadingForm />} />
          <Route path="/meter-readings/:id" element={<MeterReadingDetail />} />
          <Route path="/meter-readings/:id/edit" element={<MeterReadingForm />} />

          <Route path="/allocations" element={<AllocationList />} />
          {/* The standalone Allocation Engine screen was folded into the New/Edit
              Allocation form (meter-reading pull + auto-allocate + lapse are all
              there now) — redirect anyone with the old link bookmarked. */}
          <Route path="/allocations/engine" element={<Navigate to="/allocations/new" replace />} />
          <Route path="/allocations/new" element={<AllocationForm />} />
          <Route path="/allocations/:id/edit" element={<AllocationForm />} />
        </Route>

        {/* Invoicing, Payments & Collections, Generator Payout & Finance Dashboard */}
        <Route element={<RequirePermission module="Finance" />}>
          <Route path="/financial/dashboard" element={<FinanceDashboard />} />
          {/* Invoicing — no manual create; drafts are auto-generated on allocation approval */}
          <Route path="/invoices" element={<InvoiceList />} />
          <Route path="/invoices/:id" element={<InvoiceDetail />} />
          <Route path="/invoices/:id/edit" element={<InvoiceForm />} />

          <Route path="/payments" element={<PaymentList />} />
          <Route path="/payments/new" element={<PaymentForm />} />
          <Route path="/payments/:id" element={<PaymentDetail />} />
          <Route path="/payments/:id/edit" element={<PaymentForm />} />

          {/* Payout module commented out */}
          {/* <Route path="/payouts" element={<PayoutList />} />
          <Route path="/payouts/new" element={<PayoutForm />} />
          <Route path="/payouts/:id/edit" element={<PayoutForm />} /> */}
        </Route>

        {/* Daily Call Report — read-only view of lead/customer discussion history */}
        <Route element={<RequirePermission module="DCR" />}>
          <Route path="/dcr" element={<DCRList />} />
          <Route path="/dcr/:id" element={<DCRDetail />} />
        </Route>

        {/* Generator Management */}
        <Route element={<RequirePermission module="Generator" />}>
          <Route path="/master/generators"                element={<GeneratorMaster />} />
          <Route path="/master/generators/new"            element={<GeneratorMaster />} />
          <Route path="/master/generators/:id/edit"       element={<GeneratorMaster />} />
          <Route path="/master/generators/:id/view"       element={<GeneratorMaster />} />
        </Route>

        {/* Consumer Management */}
        <Route element={<RequirePermission module="Consumer" />}>
          <Route path="/master/customers"                 element={<CustomerMaster />} />
          <Route path="/master/customers/new"             element={<CustomerMaster />} />
          <Route path="/master/customers/:id/edit"        element={<CustomerMaster />} />
          <Route path="/master/customers/:id/view"        element={<CustomerMaster />} />
        </Route>

        {/* Masters & Admin — each master has list + /new + /:id/edit full-page routes */}
        <Route element={<RequirePermission module="Masters" />}>
        <Route path="/master/tariffs"                   element={<TariffMaster />} />
        <Route path="/master/tariffs/new"               element={<TariffMaster />} />
        <Route path="/master/tariffs/:id/edit"          element={<TariffMaster />} />
        <Route path="/master/tneb-categories"           element={<TnebCategoryMaster />} />
        <Route path="/master/tneb-categories/new"       element={<TnebCategoryMaster />} />
        <Route path="/master/tneb-categories/:id/edit"  element={<TnebCategoryMaster />} />
        <Route path="/master/tneb-tariffs"              element={<TnebTariffMaster />} />
        <Route path="/master/tneb-tariffs/new"          element={<TnebTariffMaster />} />
        <Route path="/master/tneb-tariffs/:id/edit"     element={<TnebTariffMaster />} />
        <Route path="/master/gst"                       element={<GSTMaster />} />
        <Route path="/master/gst/new"                   element={<GSTMaster />} />
        <Route path="/master/gst/:id/edit"              element={<GSTMaster />} />
        <Route path="/master/slots"                     element={<SlotMaster />} />
        <Route path="/master/slots/new"                 element={<SlotMaster />} />
        <Route path="/master/slots/:id/edit"            element={<SlotMaster />} />
        <Route path="/master/proposal-templates"        element={<ProposalTemplatesMaster />} />
        <Route path="/master/proposal-templates/new"    element={<ProposalTemplatesMaster />} />
        <Route path="/master/proposal-templates/:id/edit" element={<ProposalTemplatesMaster />} />
        <Route path="/master/email-templates"           element={<EmailTemplateMaster />} />
        <Route path="/master/email-templates/new"       element={<EmailTemplateMaster />} />
        <Route path="/master/email-templates/:id/edit"  element={<EmailTemplateMaster />} />
        <Route path="/master/whatsapp-templates"          element={<WhatsAppTemplateMaster />} />
        <Route path="/master/whatsapp-templates/new"      element={<WhatsAppTemplateMaster />} />
        <Route path="/master/whatsapp-templates/:id/edit" element={<WhatsAppTemplateMaster />} />
        <Route path="/master/code-masters"           element={<CodeMaster />} />
        <Route path="/master/code-masters/new"       element={<CodeMaster />} />
        <Route path="/master/code-masters/:id/edit"  element={<CodeMaster />} />
        <Route path="/master/users"                     element={<UserMaster />} />
        <Route path="/master/users/new"                 element={<UserMaster />} />
        <Route path="/master/users/:id/edit"            element={<UserMaster />} />
        <Route path="/master/roles"                     element={<RoleMaster />} />
        <Route path="/master/roles/new"                 element={<RoleMaster />} />
        <Route path="/master/roles/:id/edit"            element={<RoleMaster />} />
        <Route path="/master/workflow"                  element={<WorkflowMaster />} />
        <Route path="/master/backup"                    element={<BackupPage />} />
        <Route path="/master/power-types"               element={<PowerTypeMaster />} />
        <Route path="/master/power-types/new"           element={<PowerTypeMaster />} />
        <Route path="/master/power-types/:id/edit"      element={<PowerTypeMaster />} />
        <Route path="/master/supply-modes"              element={<SupplyModeMaster />} />
        <Route path="/master/supply-modes/new"          element={<SupplyModeMaster />} />
        <Route path="/master/supply-modes/:id/edit"     element={<SupplyModeMaster />} />
        <Route path="/master/edcs"                      element={<EdcMaster />} />
        <Route path="/master/edcs/new"                  element={<EdcMaster />} />
        <Route path="/master/edcs/:id/edit"             element={<EdcMaster />} />
        <Route path="/master/injecting-voltages"        element={<InjectingVoltageMaster />} />
        <Route path="/master/injecting-voltages/new"    element={<InjectingVoltageMaster />} />
        <Route path="/master/injecting-voltages/:id/edit" element={<InjectingVoltageMaster />} />
        <Route path="/master/drawal-voltages"           element={<DrawalVoltageMaster />} />
        <Route path="/master/drawal-voltages/new"       element={<DrawalVoltageMaster />} />
        <Route path="/master/drawal-voltages/:id/edit"  element={<DrawalVoltageMaster />} />
        <Route path="/master/agreement-types"                  element={<AgreementTypeMaster />} />
        <Route path="/master/agreement-types/new"              element={<AgreementTypeMaster />} />
        <Route path="/master/agreement-types/:id/edit"         element={<AgreementTypeMaster />} />
        <Route path="/master/bank-accounts"                    element={<BankMaster />} />
        <Route path="/master/bank-accounts/new"                element={<BankMaster />} />
        <Route path="/master/bank-accounts/:id/edit"           element={<BankMaster />} />
        </Route>

        {/* Reports */}
        <Route element={<RequirePermission module="Reports" />}>
          <Route path="/reports/lead-conversion" element={<LeadConversionReport />} />
          <Route path="/reports/sales-performance" element={<SalesPerformanceReport />} />
          <Route path="/reports/revenue" element={<RevenueReport />} />
          <Route path="/reports/gst" element={<GSTReportReport />} />
          <Route path="/reports/pl" element={<PAndLReport />} />
        </Route>
        </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default AppRoutes
